/**
 * @file     sharedGeolocation.ts
 * @purpose  Shared permission + coordinate store for geolocation. Single source of truth across map surfaces (MapboxNeighborhoodMap, LocationPromptToast) so users are never prompted twice.
 */

export type GeoPermission = 'granted' | 'denied' | null;

export const GEO_PERMISSION_STORAGE_KEY = 'relta.heroMapGeo';
export const GEO_COORDS_STORAGE_KEY = 'relta.geoCoords';
export const GEO_CLASSIFY_STORAGE_KEY = 'relta.geoClassify';

export type StoredCoords = { lng: number; lat: number; ts: number };

export function readStoredGeoPermission(): GeoPermission {
  try {
    const v = localStorage.getItem(GEO_PERMISSION_STORAGE_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredGeoPermission(state: 'granted' | 'denied'): void {
  try {
    localStorage.setItem(GEO_PERMISSION_STORAGE_KEY, state);
  } catch {
    // ignore
  }
}

export function readStoredCoords(maxAgeMs = 10 * 60 * 1000): StoredCoords | null {
  try {
    const raw = localStorage.getItem(GEO_COORDS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredCoords;
    if (Date.now() - parsed.ts > maxAgeMs) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredCoords(coords: { lng: number; lat: number }): void {
  try {
    localStorage.setItem(GEO_COORDS_STORAGE_KEY, JSON.stringify({ ...coords, ts: Date.now() }));
  } catch {
    // ignore
  }
}

// Metro Vancouver bounding box, used for fast in-region check before any reverse geocoding.
export const METRO_VAN_BBOX = { west: -124.5, east: -121.2, south: 48.8, north: 50.4 } as const;

export function isInMetroVancouverBbox(lng: number, lat: number): boolean {
  return (
    lat > METRO_VAN_BBOX.south &&
    lat < METRO_VAN_BBOX.north &&
    lng > METRO_VAN_BBOX.west &&
    lng < METRO_VAN_BBOX.east
  );
}

// Schedule non-critical work after the browser is idle. Falls back to a small timeout so we
// never run heavy geolocation work on the LCP/FCP critical path.
export function scheduleIdle(cb: () => void, timeoutMs = 1500): () => void {
  if (typeof window === 'undefined') return () => {};
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(cb, { timeout: timeoutMs });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, 200);
  return () => window.clearTimeout(id);
}
